import { useCallback, useEffect, useState } from "react";
import { ApiError } from "@forcebase-ai/api-client";

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/** Loads async data on mount / when `deps` change, with a manual reload. Error messages are the
 * API's PHI-safe message only. Pass `pollMs` to silently re-fetch on an interval — no loading
 * flicker, errors ignored (the next tick retries), paused while the tab is hidden. */
export function useAsync<T>(
  fn: () => Promise<T>,
  deps: unknown[],
  opts?: { pollMs?: number },
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const run = useCallback(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => active && setData(d))
      .catch((e: unknown) => {
        if (!active) return;
        setError(e instanceof ApiError ? e.message : "Something went wrong");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // Keyed off caller-provided deps + reload nonce (fn identity is unstable by design).
  }, [...deps, nonce]);

  useEffect(run, [run]);

  // Background poll: refreshes data in place so chat/status screens stay live.
  const pollMs = opts?.pollMs ?? 0;
  useEffect(() => {
    if (!pollMs) return;
    const t = setInterval(() => {
      if (document.hidden) return;
      fn()
        .then(setData)
        .catch(() => undefined);
    }, pollMs);
    return () => clearInterval(t);
    // Same intentional keying as `run` — fn identity is unstable by design.
  }, [...deps, pollMs]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}
